import dayjs from 'dayjs';
import { useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SessionView } from '../../services/sessionService';

export const SESSION_PAGE_SIZE = 20;

interface SessionTableProps {
  rows: SessionView[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  canEnd: boolean;
  onEnd: (sid: string) => void;
}

const formatTime = (value: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const part = (p: { name: string; version: string | null } | null) =>
  p ? [p.name, p.version].filter(Boolean).join(' ') : '';

// "Chrome 141.0.0.0 · macOS 10.15.7 · desktop"; empty when the server parsed nothing.
function describeDevice(row: SessionView): string {
  return [part(row.browser), part(row.os), row.device?.type].filter(Boolean).join(' · ');
}

const STATUS_COLOR = { active: 'success', revoked: 'default', expired: 'warning' } as const;

export function SessionTable({ rows, total, page, onPageChange, loading, canEnd, onEnd }: SessionTableProps) {
  const t = useTranslate();

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.session.device')}</TableCell>
              <TableCell>{t('module.session.ip')}</TableCell>
              <TableCell>{t('module.session.signedIn')}</TableCell>
              <TableCell>
                <Tooltip title={t('module.session.lastActiveHint')}>
                  <span>{t('module.session.lastActive')}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{t('module.session.expires')}</TableCell>
              <TableCell>{t('module.session.status')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const device = describeDevice(row);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title={row.userAgent || ''}>
                      <span>{device || t('module.session.unknownDevice')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.ip || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(row.createdAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(row.lastUsedAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(row.expiresAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[row.status]}
                      label={t(`module.session.${row.status}`)}
                    />
                    {row.current && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={t('module.session.thisSession')}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canEnd && row.status === 'active' && !row.current && (
                      <Button size="small" color="error" onClick={() => onEnd(row.id)}>
                        {t('module.session.end')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('module.session.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      <TablePagination
        component="div"
        count={total}
        page={Math.max(page - 1, 0)}
        rowsPerPage={SESSION_PAGE_SIZE}
        rowsPerPageOptions={[SESSION_PAGE_SIZE]}
        onPageChange={(_event, zeroBased) => onPageChange(zeroBased + 1)}
      />
    </Box>
  );
}
